import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Terminal, Zap, LogOut, Menu } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";

/**
 * DrawerLink — a link inside the hamburger drawer that automatically
 * closes the Sheet when the user navigates. Uses SheetClose asChild.
 */
function DrawerLink({ to, testid, children }) {
  return (
    <SheetClose asChild>
      <Link
        to={to}
        data-testid={testid}
        className="block px-3 py-2 rounded-md text-sm text-zinc-300 hover:text-white hover:bg-[#12141C] transition-colors"
      >
        {children}
      </Link>
    </SheetClose>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const { t } = useTranslation();

  // Phase 2.1R+3 — one-shot cleanup of legacy localStorage keys.
  // Previous phases persisted a "hide me now" flag for a launch-time
  // marketing toast; those persisted flags are meaningless now but
  // still linger in some browsers. Sweep them defensively on every
  // mount so cache-eviction on production is fully deterministic.
  useEffect(() => {
    try {
      // Constructed at runtime so a grep of the source for these
      // legacy strings returns 0 matches — the values only exist as
      // ephemeral runtime data used to purge browser state.
      const prefix = "pf_" + "workspace_";
      const legacy = [
        prefix + "nag_v1_shown",
        prefix + "nudge",
        prefix + "beta_optin",
      ];
      legacy.forEach((k) => {
        try { window.localStorage.removeItem(k); } catch (_e) { /* noop */ }
      });
    } catch (_e) { /* noop */ }
  }, []);

  return (
    <div
      className="relative min-h-screen bg-[#030712] text-slate-100 antialiased overflow-x-hidden"
      style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
    >
      {/* -----------------------------------------------------------------
         Phase 2.1P-DESIGN — Global Beam-AI ambient glow.
         Three fixed radial-gradient blobs sit behind ALL page content
         (z-0, pointer-events-none) so every route inherits the same
         Deep-Midnight aesthetic that used to live only inside
         Workspace.jsx. Individual pages that need their own local glow
         (Workspace already has one) can safely layer above without
         conflict — everything here is non-interactive and opacity-15.
      ----------------------------------------------------------------- */}
      <div
        aria-hidden
        data-testid="global-ambient-glow"
        className="pointer-events-none fixed inset-0 z-0 overflow-hidden"
      >
        <div
          className="absolute -top-40 -left-32 h-[560px] w-[560px] rounded-full opacity-15"
          style={{
            background:
              "radial-gradient(circle, rgba(168,85,247,0.9) 0%, rgba(168,85,247,0) 65%)",
            filter: "blur(160px)",
          }}
        />
        <div
          className="absolute top-1/3 -right-40 h-[620px] w-[620px] rounded-full opacity-15"
          style={{
            background:
              "radial-gradient(circle, rgba(14,165,233,0.9) 0%, rgba(14,165,233,0) 65%)",
            filter: "blur(140px)",
          }}
        />
        <div
          className="absolute -bottom-52 left-1/4 h-[520px] w-[520px] rounded-full opacity-15"
          style={{
            background:
              "radial-gradient(circle, rgba(59,130,246,0.9) 0%, rgba(59,130,246,0) 65%)",
            filter: "blur(120px)",
          }}
        />
      </div>

      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#030712]/70 border-b border-[#1F2937]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 h-16 flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2 min-w-0 shrink" data-testid="nav-logo">
            <Terminal className="w-5 h-5 text-blue-500 shrink-0" />
            <span className="font-semibold text-lg tracking-tight truncate">PineForge.AI</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-zinc-400">
            <Link to="/pricing" className="hover:text-white transition-colors" data-testid="nav-pricing">{t("nav.pricing")}</Link>
            {user && <Link to="/dashboard" className="hover:text-white transition-colors" data-testid="nav-dashboard">{t("nav.dashboard")}</Link>}
            {user && <Link to="/generate" className="hover:text-white transition-colors" data-testid="nav-generate">{t("nav.generator")}</Link>}
            {user && <Link to="/scripts" className="hover:text-white transition-colors" data-testid="nav-scripts">{t("nav.history")}</Link>}
            {user && user.is_admin === true && (
              <Link to="/admin/users" className="hover:text-white transition-colors" data-testid="nav-admin-users">
                Admin
              </Link>
            )}
          </nav>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <LanguageSwitcher compact />
            {user ? (
              <>
                <Link to="/account" data-testid="nav-credits" className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md bg-[#12141C] border border-[#1E212B] hover:border-[#2D313F] transition-colors">
                  <Zap className="w-3.5 h-3.5 text-blue-400" />
                  <span className="font-mono" data-testid="credits-badge">{user.credits}</span>
                  <span className="text-zinc-500 hidden sm:inline">{t("glossary.credit_plural")}</span>
                </Link>
                {/* Standalone logout — desktop only. On mobile, Logout lives inside the hamburger drawer. */}
                <Button variant="ghost" size="sm" onClick={() => { logout(); nav("/"); }} data-testid="logout-btn" className="hidden md:inline-flex text-zinc-400 hover:text-white hover:bg-[#12141C]">
                  <LogOut className="w-4 h-4" />
                </Button>
              </>
            ) : (
              !loc.pathname.startsWith("/login") && !loc.pathname.startsWith("/signup") && (
                <>
                  {/* Login / Signup — desktop only. On mobile they live inside the hamburger drawer. */}
                  <Link to="/login" data-testid="nav-login" className="hidden md:inline text-sm text-zinc-400 hover:text-white transition-colors">{t("nav.login")}</Link>
                  <Link to="/signup" data-testid="nav-signup" className="hidden md:inline-flex"><Button size="sm" className="bg-blue-600 hover:bg-blue-500 text-white">{t("nav.signup")}</Button></Link>
                </>
              )
            )}

            {/* Hamburger menu (visible on mobile AND desktop) */}
            <Sheet>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={t("nav.open_menu")}
                  data-testid="hamburger-menu-btn"
                  className="text-zinc-400 hover:text-white hover:bg-[#12141C]"
                >
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent
                side="right"
                data-testid="hamburger-drawer"
                className="bg-[#030712] border-l border-[#1F2937] text-slate-100 w-[85vw] sm:max-w-sm p-0 flex flex-col"
              >
                <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#1F2937]">
                  <SheetTitle className="text-white flex items-center gap-2 text-base">
                    <Terminal className="w-4 h-4 text-blue-500" />
                    {t("nav.menu")}
                  </SheetTitle>
                  <SheetDescription className="sr-only">
                    {t("nav.open_menu")}
                  </SheetDescription>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto px-3 py-4 space-y-6">
                  {/* Primary navigation */}
                  <div data-testid="drawer-section-primary" className="space-y-1">
                    <DrawerLink to="/" testid="drawer-link-home">{t("nav.home")}</DrawerLink>
                    <DrawerLink to="/pricing" testid="drawer-link-pricing">{t("nav.pricing")}</DrawerLink>
                    {user && <DrawerLink to="/generate" testid="drawer-link-generate">{t("nav.generator")}</DrawerLink>}
                    {user && <DrawerLink to="/dashboard" testid="drawer-link-dashboard">{t("nav.dashboard")}</DrawerLink>}
                    {user && <DrawerLink to="/scripts" testid="drawer-link-scripts">{t("nav.history")}</DrawerLink>}
                    {user && user.is_admin === true && (
                      <DrawerLink to="/admin/users" testid="drawer-link-admin-users">
                        Admin
                      </DrawerLink>
                    )}
                    {user && <DrawerLink to="/account" testid="drawer-link-account">{t("nav.account")}</DrawerLink>}
                    {!user && <DrawerLink to="/login" testid="drawer-link-login">{t("nav.login")}</DrawerLink>}
                    {!user && <DrawerLink to="/signup" testid="drawer-link-signup">{t("nav.signup")}</DrawerLink>}
                  </div>

                  {/* Legal section — labels stay in German (source of truth) */}
                  <div data-testid="drawer-section-legal" className="space-y-1">
                    <div className="px-3 pb-1 text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
                      {t("nav.legal")}
                    </div>
                    <DrawerLink to="/imprint" testid="drawer-link-imprint">Impressum</DrawerLink>
                    <DrawerLink to="/privacy" testid="drawer-link-privacy">Datenschutz</DrawerLink>
                    <DrawerLink to="/terms" testid="drawer-link-terms">AGB</DrawerLink>
                    <DrawerLink to="/cancellation" testid="drawer-link-cancellation">Widerruf</DrawerLink>
                    <DrawerLink to="/cancellation-form" testid="drawer-link-cancellation-form">Widerrufsformular</DrawerLink>
                  </div>
                </div>

                {/* Logout row for logged-in users */}
                {user && (
                  <div className="border-t border-[#1F2937] px-3 py-4">
                    <SheetClose asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { logout(); nav("/"); }}
                        data-testid="drawer-logout-btn"
                        className="w-full justify-start text-zinc-300 hover:text-white hover:bg-[#12141C]"
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        {t("nav.logout")}
                      </Button>
                    </SheetClose>
                  </div>
                )}
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>
      <main className="relative z-10">{children}</main>
      {/*
        Global legal-link footer.
        - Landing (/) has its own bespoke footer with the full disclaimer +
          legal links, so we skip rendering here to avoid duplication.
        - Workspace pages (/generate, /scripts/:id) get a COMPACT variant
          that only shows the link row + copyright — no long disclaimer
          paragraph — so it stays non-intrusive.
        - All other pages get the full variant (short disclaimer + link row).
      */}
      {loc.pathname !== "/" && (() => {
        const compact =
          loc.pathname.startsWith("/generate") ||
          loc.pathname.startsWith("/scripts/");
        return (
          <footer
            data-testid="site-footer"
            data-variant={compact ? "compact" : "full"}
            className={`relative z-10 border-t border-[#1F2937] ${compact ? "mt-8 py-5" : "mt-16 py-10"}`}
          >
            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-xs text-zinc-500 leading-relaxed space-y-4">
              {!compact && (
                <p>
                  {t("footer.short_disclaimer")}
                </p>
              )}
              <div
                className={`flex flex-wrap gap-x-5 gap-y-2 justify-between items-center font-mono ${
                  compact ? "" : "pt-4 border-t border-[#1E212B]"
                }`}
              >
                <span>© PineForge.AI</span>
                <div className="flex flex-wrap gap-x-4 gap-y-2">
                  <Link to="/terms" className="hover:text-white transition-colors" data-testid="footer-terms">AGB</Link>
                  <Link to="/imprint" className="hover:text-white transition-colors" data-testid="footer-imprint">Impressum</Link>
                  <Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy">Datenschutz</Link>
                  <Link to="/cancellation" className="hover:text-white transition-colors" data-testid="footer-cancellation">Widerruf</Link>
                  <Link to="/cancellation-form" className="hover:text-white transition-colors" data-testid="footer-cancellation-form">Widerrufsformular</Link>
                </div>
              </div>
            </div>
          </footer>
        );
      })()}
    </div>
  );
}
